/** §4.2 — SDK'nın ürettiği olay tipleri. */
export const EVENT_TYPES = [
  'page_view',
  'page_leave',
  'click',
  'rage_click',
  'dead_click',
  'scroll_depth',
  'form_focus',
  'form_error',
  'input_retry',
  'form_submit',
  'js_error',
  'api_error',
  'web_vitals',
  'custom',
] as const;

export type EventType = (typeof EVENT_TYPES)[number];

/**
 * §4.2 sonu — e-ticaret olayları SDK'da YOKTUR; worker tarafında türetilir (§11.4).
 * Boş string "türetilmemiş" anlamına gelir (ClickHouse DEFAULT '').
 */
export const DERIVED_TYPES = [
  'product_view',
  'add_to_cart',
  'begin_checkout',
  'checkout_step',
  'purchase',
] as const;

export type DerivedType = (typeof DERIVED_TYPES)[number];

/** §6 — UA parse sonucu cihaz sınıfı. */
export const DEVICES = ['mobile', 'desktop', 'tablet'] as const;
export type Device = (typeof DEVICES)[number];

/** §6.1 — kanal sınıflandırma çıktısı (sıralı ilk eşleşme). */
export const CHANNELS = [
  'paid',
  'email',
  'sms',
  'organic_search',
  'organic_social',
  'direct',
  'referral',
] as const;
export type Channel = (typeof CHANNELS)[number];

/** §12.1 */
export const SEVERITIES = ['critical', 'high', 'medium', 'low'] as const;
export type Severity = (typeof SEVERITIES)[number];

/** §8.2 insights.status + §12.4 yaşam döngüsü. */
export const INSIGHT_STATUSES = ['new', 'seen', 'applied', 'dismissed', 'resolved'] as const;
export type InsightStatus = (typeof INSIGHT_STATUSES)[number];

/** §8.2 goals.status */
export const GOAL_STATUSES = ['proposed', 'confirmed', 'rejected'] as const;
export type GoalStatus = (typeof GOAL_STATUSES)[number];

/** §19.1 maliyet profili */
export const COST_PROFILES = ['eco', 'balanced', 'performance'] as const;
export type CostProfile = (typeof COST_PROFILES)[number];

/** §5 / §10 taşıma sabitleri */
export const KAFKA_TOPIC_RAW = 'dxp.events.raw';
export const KAFKA_TOPIC_PARTITIONS = 6;
export const KAFKA_CONSUMER_GROUP = 'dxp-worker';

/** §10 — /collect limitleri */
export const MAX_EVENTS_PER_REQUEST = 100;
export const MAX_REQUEST_BYTES = 256 * 1024;
export const RATE_LIMIT_PER_PID_PER_MIN = 1000;

/** §6 — oturum boşluk eşiği */
export const SESSION_GAP_MS = 30 * 60 * 1000;

/** §9 — alan uzunluk bütçeleri */
export const MAX_URL_LEN = 512;
export const MAX_SELECTOR_LEN = 120;
export const MAX_STACK_HEAD_LEN = 500;

/** §9 — SDK gzip boyut bütçesi */
export const SDK_GZIP_BUDGET_BYTES = 30720;

/* ------------------------- Faz 2 §22 — session replay ------------------------- */

/** §22.2 — ayrı lazy bundle `dist/replay.js` gzip bütçesi. */
export const REPLAY_GZIP_BUDGET_BYTES = 24576;

/** §22.5 — SDK'nın chunk üretim hedefi (bayt). */
export const REPLAY_CHUNK_TARGET_BYTES = 65536;

/** §22.3 — mod=buffer ring buffer tavanı (bayt); dolunca en eski chunk düşer. */
export const REPLAY_BUFFER_MAX_BYTES = 1572864;

/** §22.3 — tetik anında geriye dönük yüklenen tampon penceresi (ms). */
export const REPLAY_LOOKBACK_MS = 60000;

/** §22.5 — `POST /replay` gövde tavanı (bayt); aşımda 413. */
export const REPLAY_POST_MAX_BYTES = 262144;

/** §22.5 — `POST /replay` pid başına dakikalık istek tavanı. */
export const REPLAY_RATE_LIMIT_PER_MIN = 300;

/** §22.3 — chunk'ın neden yüklendiği. `sampled` = mod=full akışı. */
export const REPLAY_TRIGGERS = [
  'sampled',
  'js_error',
  'api_error',
  'rage_click',
  'dead_click',
  'abandon',
] as const;
export type ReplayTrigger = (typeof REPLAY_TRIGGERS)[number];

/** §22.3 — kayıt modu. */
export const REPLAY_MODES = ['full', 'buffer'] as const;
export type ReplayMode = (typeof REPLAY_MODES)[number];

/* -------------------------- Faz 2 §24 — SEO tarayıcı -------------------------- */

/**
 * §24.3 / §21.6 — crawler'ın gönderdiği User-Agent. Tek kaynak buradadır: tarayıcı
 * bu değeri gönderir, robots.txt eşleşmesi aynı değerin ürün jetonuyla yapılır.
 */
export const SEO_USER_AGENT = 'DXP-SeoBot/1.0';

/**
 * §24.3 — robots.txt `User-agent:` grup jetonu (UA'nın sürümsüz ürün adı).
 * robots.txt grupları sürüm taşımaz ("DXP-SeoBot/1.0" yazan site kuralı asla eşleşmezdi),
 * bu yüzden eşleşme jetonu ayrı sabittir.
 */
export const SEO_BOT_TOKEN = 'DXP-SeoBot';

/**
 * §19.1 — Maliyet profili tablosu. Tek kaynak: profil, tüm alt sistemlerin ayarlarını belirler.
 * `projects.cost_overrides` JSONB ile alan bazında override edilebilir.
 */
export interface CostProfileSettings {
  /** Hot (hızlı disk) dönemi — ay */
  hotMonths: number;
  /** Warm→arşiv geçişi — ay */
  archiveAfterMonths: number;
  /** web_vitals örnekleme oranı 0..1 */
  webVitalsSampleRate: number;
  /** scroll_depth / click x_pct-y_pct örnekleme oranı 0..1 */
  pointerDetailSampleRate: number;
  /** Insight job sıklığı — ms */
  insightIntervalMs: number;
  /** Yönetici raporu yenileme sıklığı */
  execReportCadence: 'weekly' | 'nightly';
  /** §22.3 — oturumun mod=full (baştan kaydet) seçilme olasılığı 0..1 */
  replaySampleRate: number;
  /** §22.3 — oturum başına yüklenebilecek toplam replay baytı; aşımda kayıt durur */
  replaySessionMaxBytes: number;
  /**
   * §25.5 — LLM anlatısının üretim sıklığı.
   * `insight-değişimi` tetiklemesi HER profilde açıktır; bu alan yalnız ZAMANLANMIŞ
   * koşuyu yönetir (eco'da haftada bir, Pzt 03:30).
   */
  narrativeCadence: 'weekly' | 'nightly';
}

export const COST_PROFILE_SETTINGS: Record<CostProfile, CostProfileSettings> = {
  eco: {
    hotMonths: 2,
    archiveAfterMonths: 6,
    webVitalsSampleRate: 0.2,
    pointerDetailSampleRate: 0.5,
    insightIntervalMs: 30 * 60 * 1000,
    execReportCadence: 'weekly',
    // §22.3 — eco'da hiçbir oturum baştan kaydedilmez; yalnız tetikle yüklenir.
    replaySampleRate: 0,
    replaySessionMaxBytes: 1048576,
    narrativeCadence: 'weekly',
  },
  balanced: {
    hotMonths: 3,
    archiveAfterMonths: 13,
    webVitalsSampleRate: 0.5,
    pointerDetailSampleRate: 1,
    insightIntervalMs: 5 * 60 * 1000,
    execReportCadence: 'nightly',
    replaySampleRate: 0.1,
    replaySessionMaxBytes: 2621440,
    narrativeCadence: 'nightly',
  },
  performance: {
    hotMonths: 6,
    archiveAfterMonths: 25,
    webVitalsSampleRate: 1,
    pointerDetailSampleRate: 1,
    insightIntervalMs: 5 * 60 * 1000,
    execReportCadence: 'nightly',
    replaySampleRate: 0.5,
    replaySessionMaxBytes: 5242880,
    narrativeCadence: 'nightly',
  },
};

export function resolveCostProfile(
  profile: CostProfile,
  overrides: Partial<CostProfileSettings> = {},
): CostProfileSettings {
  return { ...COST_PROFILE_SETTINGS[profile], ...overrides };
}

/* -------------------------------------------------------------------------- */
/* §25 LLM anlatı katmanı (§21.6)                                              */
/* -------------------------------------------------------------------------- */

/** §25.5 — tek anlatı çağrısının azami çıktı token'ı. */
export const LLM_MAX_TOKENS = 1200;
/** §25.5 — pid başına GÜNLÜK üretim tavanı (manuel "Yenile" DAHİL). */
export const LLM_DAILY_RUNS_PER_PID = 2;
/** §25.5 — takvim ayı (UTC) toplam maliyet tavanı, USD. */
export const LLM_MONTHLY_USD_CAP = 5;
/** §25.5 — girdi token'ı milyon başına USD (claude-haiku-4-5 liste fiyatı). */
export const LLM_USD_PER_MTOK_IN = 1;
/** §25.5 — çıktı token'ı milyon başına USD. */
export const LLM_USD_PER_MTOK_OUT = 5;
/** §25.2 — varsayılan model. */
export const LLM_DEFAULT_MODEL = 'claude-haiku-4-5';
/** §25.2 — Anthropic Messages API sürüm başlığı. */
export const LLM_ANTHROPIC_VERSION = '2023-06-01';
/** §25.2 — tek istek zaman aşımı (ms). */
export const LLM_TIMEOUT_MS = 30000;
/** §25.2 — 429/5xx sonrası tek tekrar öncesi bekleme (ms). */
export const LLM_RETRY_DELAY_MS = 10000;
/** §25.4 — sistem prompt sözleşmesi: toplam azami karakter. */
export const LLM_MAX_NARRATIVE_CHARS = 900;

/* -------------------------------------------------------------------------- */
/* §26 E-posta raporu (§21.6 sabitler tablosu)                                 */
/* -------------------------------------------------------------------------- */

/**
 * §26.6 / §21.6 — pid başına abone tavanı; aşımda 409.
 *
 * NEDEN tavan: uç ADMIN_TOKEN'ın arkasında olsa da, POST her çağrıda bir onay
 * e-postası kuyruğa alır; tavansız bir uç toplu istenmeyen posta aracına dönüşürdü.
 * 20, hedef persona (tek e-ticaret ekibi) için bol bir sınırdır (§26.6 KARARI).
 */
export const EMAIL_MAX_SUBS_PER_PID = 20;

/**
 * §26.6 — e-posta adresi uzunluk tavanı (RFC 5321 yol/adres sınırı 254 oktet).
 * Doğrulama regex'inden ÖNCE bakılır: uzunluk kontrolü sabit maliyetlidir ve
 * patolojik uzun girdinin desen eşlemesine hiç girmemesini sağlar.
 */
export const EMAIL_MAX_ADDRESS_LEN = 254;

/** §26.3 — abonelik sıklığı; varsayılan `weekly`. Şema CHECK'i ile aynı küme (§26.4). */
export const EMAIL_CADENCES = ['weekly', 'daily'] as const;
export type EmailCadence = (typeof EMAIL_CADENCES)[number];

/* -------------------------------------------------------------------------- */
/* §27 Çoklu kullanıcı ve yetkilendirme (§21.6 sabitler tablosu)               */
/* -------------------------------------------------------------------------- */

/**
 * §27.1 — TAM SIRALI rol kümesi: `viewer < editor < owner`. Şema CHECK'i ile aynı
 * küme (009_auth.sql `memberships.role` / `invites.role`).
 */
export const ROLES = ['viewer', 'editor', 'owner'] as const;
export type Role = (typeof ROLES)[number];

/**
 * §27.5 — RBAC karşılaştırması sayısaldır: `ROLE_ORDER[sahip] >= ROLE_ORDER[gerekli]`.
 * Dizi indeksiyle (`ROLES.indexOf`) hesaplanmaz — tablo AÇIKÇA yazıldığında sıralamayı
 * bozan bir yeniden düzenleme tip hatası verir, sessiz bir yetki genişlemesi değil.
 */
export const ROLE_ORDER: Record<Role, number> = { viewer: 0, editor: 1, owner: 2 };

/**
 * §27.3 / §21.2 — bcrypt maliyet katsayısı. argon2/Passport/JWT EKLENMEZ; tek hash
 * kütüphanesi `bcryptjs`tir (saf JS, derleme gerektirmez).
 *
 * 12 seçimi: modern donanımda hash ~250 ms sürer. Login ucu §27.4 rate limit'iyle
 * (5 deneme / 15 dk) korunduğu için bu gecikme kullanıcıya değil, kaba kuvvete maliyettir.
 */
export const AUTH_BCRYPT_COST = 12;

/** §27.3 — parola politikası: en az 10 karakter. */
export const AUTH_PASSWORD_MIN_LEN = 10;

/**
 * §27.3 — parola politikası: en çok 72 karakter.
 * Üst sınırın kaynağı bcrypt'in 72 baytlık girdi tavanıdır: daha uzun girdi SESSİZCE
 * kırpılır, yani "parolamın son 20 hanesi hiç doğrulanmıyor" durumu doğar. Sınırı
 * politikaya taşımak bu sessiz davranışı görünür bir 400'e çevirir.
 */
export const AUTH_PASSWORD_MAX_LEN = 72;

/** §27.3 — Redis oturumunun KAYAN TTL'i (gün). Doğrulanan her istekte tazelenir. */
export const AUTH_SESSION_TTL_DAYS = 30;

/* -------------------------------------------------------------------------- */
/* §42 Organizasyon rolleri (Faz 4)                                            */
/* -------------------------------------------------------------------------- */

/**
 * §42.3 — ORG rolleri, PROJE rollerinden AYRI bir kümedir.
 *
 * `member < admin < owner`. `viewer/editor/owner` (ROLES) ile KARIŞTIRILMAZ: iki küme
 * iki farklı soruyu yanıtlar. Proje rolü "bu projenin VERİSİNE ne yapabilirsin"i, org
 * rolü "bu organizasyonu YÖNETEBİLİR misin"i (proje açma, üye/plan/fatura) söyler.
 *
 * §42.2 KARARI gereği org üyeliği proje verisine erişim VERMEZ — bu yüzden iki kümenin
 * ortak bir sıralaması da YOKTUR ve `ROLE_ORDER` ile karşılaştırılamazlar. Ortak
 * `owner` adı bilinçlidir (her iki uzayda da "en yetkili"), ama tipler ayrıdır:
 * `Role` beklenen yere `OrgRole` geçirmek derleme hatasıdır.
 */
export const ORG_ROLES = ['member', 'admin', 'owner'] as const;
export type OrgRole = (typeof ORG_ROLES)[number];

/** §42.5 — org RBAC karşılaştırması; `ROLE_ORDER` ile AYNI gerekçe (açık tablo). */
export const ORG_ROLE_ORDER: Record<OrgRole, number> = { member: 0, admin: 1, owner: 2 };


/** §27.3 — oturum çerezi. `httpOnly` + `SameSite=Lax` + `Path=/`. */
export const AUTH_SESSION_COOKIE = 'dxp_sess';

/**
 * §27.3 — CSRF çift savunması: cookie'li TÜM mutasyonlarda bu başlık zorunludur.
 * Değeri önemsizdir (varlığı yeter): tarayıcı cross-site bir form/img isteğine özel
 * başlık ekleyemez, ekleyebilmek için CORS ön-uçuşunu geçmesi gerekir.
 * Bearer (ADMIN_TOKEN) istekleri bu kontrolden MUAFTIR (§27.6).
 */
export const AUTH_CSRF_HEADER = 'x-dxp-csrf';

/** §27.4 — login rate limit: (ip, email) başına pencere içindeki azami deneme. */
export const AUTH_LOGIN_MAX_ATTEMPTS = 5;

/**
 * §33.1 — IP başına KÜRESEL başarısız deneme tavanı (aynı 15 dk penceresi).
 *
 * NEDEN gerekli: `AUTH_LOGIN_MAX_ATTEMPTS` anahtarı (ip, e-posta) İKİLİSİDİR; saldırgan
 * her istekte FARKLI e-posta kullanarak anahtarı değiştirir ve sınırsız deneme yapar.
 * Her deneme, hesap OLMASA BİLE bir bcrypt cost-12 hesabı ödetir (zaman denkleştirmesi
 * bunu kasten yapar) ve `bcryptjs` saf JS olduğu için ingest-api'nin olay döngüsünde
 * koşar — yani bu, /collect'in §19.4 gecikme hedefini tehdit eden bir ANALİTİK HATTI
 * sorunudur, yalnız auth sorunu değil.
 *
 * NEDEN 30 ve NEDEN yalnız BAŞARISIZ denemeler: kalabalık bir ofis NAT'ı arkasından
 * 15 dakikada 30'dan fazla BAŞARILI giriş olabilir; tüm denemeleri saysaydık meşru
 * kullanıcılar birbirini kilitlerdi. Başarısızlık saymak, meşru trafiği tavana hiç
 * yaklaştırmaz ama kaba kuvveti 30 denemede durdurur.
 */
export const AUTH_LOGIN_IP_MAX_ATTEMPTS = 30;

/** §27.4 — login rate limit penceresi (dakika). */
export const AUTH_LOGIN_WINDOW_MIN = 15;

/** §27.2 — davet token'ının ömrü (gün); `invites.expires_at` = oluşturma + bu süre. */
export const AUTH_INVITE_TTL_DAYS = 7;

/* -------------------------------------------------------------------------- */
/* §43 Self-serve kayıt (Faz 4)                                                */
/* -------------------------------------------------------------------------- */

/**
 * §43.2 — e-posta doğrulama token'ının ömrü (saat).
 *
 * Davet 7 GÜNDÜR (AUTH_INVITE_TTL_DAYS) ama doğrulama 24 SAATTİR: davet, davet
 * edilenin başka bir işi bitirmesini bekleyebilir; doğrulama ise kullanıcının o an
 * başlattığı bir akışın ortasıdır. Kısa ömür, sızmış bir linkin penceresini daraltır.
 */
export const AUTH_VERIFY_TTL_HOURS = 24;

/** §43.4 — IP başına GÜNLÜK kayıt tavanı (§33.1 sayaç makinesiyle aynı desen). */
export const SIGNUP_IP_MAX_PER_DAY = 20;


/* -------------------------------------------------------------------------- */
/* §28 Mobil SDK (§21.6 sabitler tablosu)                                      */
/* -------------------------------------------------------------------------- */

/*
 * NEDEN burada: §21.6 "tek kaynak" kuralı. `packages/mobile-sdk` bu paketi RUNTIME'da
 * import EDEMEZ (§28.1 sıfır runtime bağımlılığı), bu yüzden aynı değerleri kendi
 * `src/constants.ts` dosyasında const olarak TEKRARLAR. İki kopyanın ayrışmasını
 * `packages/mobile-sdk/src/constants.test.ts` eşitlik testi engeller (§28.6):
 * @dxp/schema orada YALNIZCA devDependency olarak, testte import edilir.
 */

/** §28.4 — mobil kuyruğun zaman tetikli boşaltma aralığı (ms). */
export const MOBILE_BATCH_MS = 10000;

/** §28.4 — mobil kuyruğun olay sayısı tetikleyicisi; bu sayıda olayda hemen gönderilir. */
export const MOBILE_BATCH_EVENTS = 30;

/**
 * §28.4 — AsyncStorage `_dxp_q` kuyruk tavanı (olay). Taşarsa EN ESKİ düşer (FIFO drop).
 * Bu kayıp §19.4'ü ihlal etmez: sıfır-kayıp zinciri `/collect` SONRASI içindir,
 * istemci tamponu web'de de best-effort'tur (MVP §9.2).
 */
export const MOBILE_QUEUE_MAX_EVENTS = 500;

/**
 * §28.5 — derlenmiş mobil paket bütçesi: `dist/*.js` toplamı, MİN EDİLMEMİŞ (bayt).
 * gzip/minify ÖLÇÜLMEZ çünkü minify'ı tüketicinin RN bundler'ı (Metro) yapar;
 * bütçe kaynak büyümesini frenlemek içindir.
 */
export const MOBILE_DIST_BUDGET_BYTES = 40960;

/* -------------------------------------------------------------------------- */
/* §33.3 — gönderim yeniden deneme politikası (M22)                            */
/* -------------------------------------------------------------------------- */

/**
 * §33.3 — 429 tek YENİDEN DENENEBİLİR 4xx'tir.
 *
 * "Çok fazla istek" geçici bir durumdur; diğer tüm 4xx'ler isteğin KENDİSİNİN bozuk
 * olduğunu söyler (400 geçersiz gövde, 401/403 yetki, 404 yanlış uç, 413 çok büyük).
 * Bunları yeniden denemek aynı hatayı sonsuza dek tekrarlar ve kuyruğu bloke eder.
 */
export const RETRYABLE_4XX: readonly number[] = [429];

/**
 * §33.3 — bu yanıt KALICI hata mı (parti düşürülmeli mi)?
 *
 * `true`  → parti DÜŞÜRÜLÜR, yeniden denenmez.
 * `false` → yeniden denenir (ağ hatası, 429, 5xx).
 *
 * Bu fonksiyon SÖZLEŞMEDİR: iki SDK da sıfır bağımlılık taşıdığı için mantığı YEREL
 * olarak tekrarlar; `transport-policy` sözleşme testleri 0..599 aralığının tamamında
 * üç uygulamanın aynı yanıtı verdiğini doğrular (§21.6 sabitlerindeki desenin aynısı).
 */
export function isPermanentFailure(status: number): boolean {
  if (!Number.isFinite(status)) return false;
  if (status < 400 || status > 499) return false;
  return RETRYABLE_4XX.indexOf(status) === -1;
}

/* -------------------------------------------------------------------------- */
/* §92 — kayıtlı segmentler (M47)                                             */
/* -------------------------------------------------------------------------- */

/**
 * §92.2 / §91.2 KARAR 1 — segment boyutları KAPALI KATALOGDUR.
 *
 * Buradaki adlar `dxp.sessions` SÜTUN adlarıdır ve SQL'e kimlik olarak yalnız
 * bu katalogtan girer — kullanıcı girdisi hiçbir zaman sütun adı olamaz.
 * Katalog `packages/schema`'da durur ki sunucu (doğrulama + SQL üretimi) ve
 * panel (seçici menüleri) AYNI sabitten okusun; iki kopya = Faz 7'nin
 * ayrışma dersi.
 */
export const SEGMENT_STRING_DIMENSIONS = [
  'device',
  'browser',
  'os',
  'channel',
  'utm_source',
] as const;

/** §92.2 — bayrak boyutları yalnız `eq` + '0'|'1' kabul eder. */
export const SEGMENT_FLAG_DIMENSIONS = ['converted', 'had_js_error', 'had_rage_click'] as const;

export const SEGMENT_DIMENSIONS = [
  ...SEGMENT_STRING_DIMENSIONS,
  ...SEGMENT_FLAG_DIMENSIONS,
] as const;

export type SegmentDimension = (typeof SEGMENT_DIMENSIONS)[number];

/** §92.2 — operatör kümesi. OR/NOT bilinçli olarak YOK (§91.2 KARAR 1). */
export const SEGMENT_OPS = ['eq', 'in'] as const;
export type SegmentOp = (typeof SEGMENT_OPS)[number];

export interface SegmentCondition {
  dim: SegmentDimension;
  op: SegmentOp;
  /** `eq` için tam 1, `in` için 1..SEGMENT_MAX_VALUES eleman. */
  values: string[];
}

/** §92.2 — koşullar YALNIZ AND ile bağlanır; alan adı bunu okunur kılar. */
export interface SegmentConditions {
  all: SegmentCondition[];
}

/** §92.2 tavanları — doğrulama sunucuda, menü boyutu panelde aynı sabitlerden. */
export const SEGMENT_MAX_CONDITIONS = 5;
export const SEGMENT_MAX_VALUES = 10;
export const SEGMENT_MAX_VALUE_LEN = 64;
export const SEGMENT_NAME_MAX_LEN = 60;
/** §91.3 — proje başına segment tavanı: açılır menü insan boyutunda kalır. */
export const SEGMENT_CAP_PER_PROJECT = 20;
